import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const WishlistContext = createContext();

function getCanonicalProductId(product) {
  if (!product) return null;
  const explicit = product.baseProductId || product.productId;
  if (explicit) return explicit;
  if (typeof product.id === 'string') return product.id.split('_')[0];
  return product.id;
}

export function WishlistProvider({ children }) {
  const [wishlistItems, setWishlistItems] = useState(() => {
    const saved = localStorage.getItem('vetcare_wishlist');
    try {
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('vetcare_wishlist', JSON.stringify(wishlistItems));
  }, [wishlistItems]);

  const wishlistMap = useMemo(() => {
    const map = new Map();
    (wishlistItems || []).forEach(item => {
      const key = item?.wishlistId || getCanonicalProductId(item);
      if (key) map.set(key, item);
    });
    return map;
  }, [wishlistItems]);

  const isInWishlist = (product) => {
    const id = getCanonicalProductId(product);
    if (!id) return false;
    return wishlistMap.has(id);
  };

  const addToWishlist = (product) => {
    const id = getCanonicalProductId(product);
    if (!id) return;

    setWishlistItems(prev => {
      const exists = (prev || []).some(p => (p?.wishlistId || getCanonicalProductId(p)) === id);
      if (exists) return prev;
      return [...(prev || []), { ...product, wishlistId: id, baseProductId: id }];
    });
  };

  const removeFromWishlist = (productOrId) => {
    const id = typeof productOrId === 'string' ? productOrId : getCanonicalProductId(productOrId);
    if (!id) return;

    setWishlistItems(prev => (prev || []).filter(p => (p?.wishlistId || getCanonicalProductId(p)) !== id));
  };

  const toggleWishlist = (product) => {
    if (isInWishlist(product)) {
      removeFromWishlist(product);
    } else {
      addToWishlist(product);
    }
  };

  const clearWishlist = () => setWishlistItems([]);

  const getWishlistCount = () => (wishlistItems || []).length;

  return (
    <WishlistContext.Provider
      value={{
        wishlistItems,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        clearWishlist,
        isInWishlist,
        getWishlistCount
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  return useContext(WishlistContext);
}
